import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/shared/services/api';

const FWD_PUBLISHABLE_KEY = import.meta.env.VITE_FWD_PUBLISHABLE_KEY;

declare global {
  interface Window {
    Forward: any;
  }
}

interface MonthlyInvoice {
  id: string;
  merchant_id: string;
  billing_month: string;
  total_fees_cents: number;
  platform_fees_cents: number;
  merchant_fees_cents: number;
  invoice_count: number;
  status: string;
  fwd_payment_intent_id: string | null;
}

export default function MonthlyPaymentPage() {
  const { monthly_invoice_id } = useParams<{ monthly_invoice_id: string }>();
  const [searchParams] = useSearchParams();
  const clientSecret = searchParams.get('secret');

  const [invoice, setInvoice] = useState<MonthlyInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const [tokenizeFunction, setTokenizeFunction] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'failed'>('pending');
  const [error, setError] = useState<string | null>(null);
  const paymentContainerRef = useRef<HTMLDivElement>(null);

  // Load Forward SDK
  useEffect(() => {
    if (document.querySelector('script[src*="forward.js"]')) {
      if (window.Forward) { setSdkLoaded(true); return; }
    }
    const script = document.createElement('script');
    script.src = 'https://sandbox-cdn.pci.getfwd.com/sdk/forward.js';
    script.onload = () => setSdkLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Load monthly invoice details
  useEffect(() => {
    if (!monthly_invoice_id) return;
    apiClient.get<MonthlyInvoice>(`/payments/monthly-invoice/${monthly_invoice_id}`)
      .then(data => setInvoice(data))
      .catch(() => setError('Invoice not found'))
      .finally(() => setLoading(false));
  }, [monthly_invoice_id]);

  // Mount Forward CardElement once SDK + invoice ready
  useEffect(() => {
    if (!sdkLoaded || !invoice || !clientSecret || paymentStatus !== 'pending') return;
    if (invoice.status !== 'pending') return;

    const mount = async () => {
      try {
        const element = await window.Forward.createCardElement({
          apiKey: FWD_PUBLISHABLE_KEY,
          clientSecret,
        });

        if (!paymentContainerRef.current) return;

        element.mount(paymentContainerRef.current, {
          onChange: (event: any) => setIsFormReady(event.ready),
          onReady: (tokenize: any) => {
            setIsFormReady(true);
            setTokenizeFunction(() => tokenize);
          },
          onSuccess: async (result: any) => {
            try {
              const paymentMethodId = result?.token || result?.payment_method_id || result?.id;
              if (!paymentMethodId) throw new Error('Missing payment method ID');

              const response = await apiClient.post<{ ok: boolean; error?: string }>(
                '/payments/process-monthly-payment',
                { monthly_invoice_id, payment_method_id: paymentMethodId }
              );

              if (!response.ok || response.error) throw new Error(response.error || 'Payment failed');
              setPaymentStatus('success');
              return Promise.resolve();
            } catch (err: any) {
              setPaymentStatus('failed');
              setError(err?.message || 'Payment failed');
              return Promise.reject(err);
            }
          },
          onError: (err: any) => {
            setPaymentStatus('failed');
            setError(err?.message || 'Payment failed. Please try again.');
          },
          style: {
            borderRadius: '8px',
            borderColor: '#e5e7eb',
            focusBorderColor: '#3b82f6',
            textColor: '#111827',
            backgroundColor: '#ffffff',
            inputBackgroundColor: '#ffffff',
          },
        });
      } catch (err: any) {
        setError(err?.message || 'Failed to initialize payment');
      }
    };

    mount();
  }, [sdkLoaded, invoice, clientSecret, paymentStatus]);

  const handlePay = async () => {
    if (!tokenizeFunction || !isFormReady) return;
    setPaymentStatus('processing');
    setError(null);
    try {
      await tokenizeFunction();
    } catch (err: any) {
      setPaymentStatus('failed');
      setError(err?.message || 'Payment failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!invoice || error === 'Invoice not found') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Invoice not found.</p>
      </div>
    );
  }

  if (invoice.status === 'paid' || paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 text-center max-w-sm w-full">
          <div className="text-5xl mb-4">✓</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Successful</h2>
          <p className="text-gray-500 text-sm">
            Your fees for {invoice.billing_month} have been paid.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <img src="/wlkthruicon.png" alt="Wlkthru" className="h-10 w-10 object-contain" />
        </div>

        <div className="text-center mb-6">
          <p className="text-base font-semibold text-gray-900">Monthly Invoice Fees</p>
          <p className="text-sm text-gray-500 mt-0.5">{invoice.billing_month}</p>
          <p className="text-xs text-gray-400 mt-1">{invoice.invoice_count} invoice{invoice.invoice_count !== 1 ? 's' : ''}</p>
        </div>

        {/* Fee breakdown */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-6">
          {invoice.platform_fees_cents > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Technology Fees:</span>
              <span className="font-medium">${(invoice.platform_fees_cents / 100).toFixed(2)}</span>
            </div>
          )}
          {invoice.merchant_fees_cents > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Merchant Fees:</span>
              <span className="font-medium">${(invoice.merchant_fees_cents / 100).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold border-t pt-2">
            <span>Total Due:</span>
            <span className="text-red-600">${(invoice.total_fees_cents / 100).toFixed(2)} USD</span>
          </div>
        </div>

        {/* Forward Card Element */}
        <div ref={paymentContainerRef} className="min-h-[180px] mb-4" />

        {error && (
          <p className="text-red-500 text-sm text-center mb-3">{error}</p>
        )}

        <button
          onClick={handlePay}
          disabled={!isFormReady || paymentStatus === 'processing'}
          className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {paymentStatus === 'processing' ? 'Processing...' : `Pay $${(invoice.total_fees_cents / 100).toFixed(2)}`}
        </button>

        <p className="text-xs text-gray-300 text-center mt-4">Secure payment · Powered by Wlkthru</p>
      </div>
    </div>
  );
}
